import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import BigNumber from 'bignumber.js'
import { useAtomValue } from 'jotai/react'
import { debounce } from 'lodash'
import { ChevronDown } from 'lucide-react'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'

import { Iconloading, Slip, Wallet } from '@/components/svgr/common'
import SolanaSrc from '@/components/svgr/solana.svg'
import { Logo } from '@/assets/images/logo'
import { LoginStatus } from '@/constants/common'
import { ITxSettingProps, SUPPORT_RPC_ENDPOINT } from '@/constants/settings'
import { SupportValue, TradeBase, TradeType } from '@/constants/trade'
import useBetSdkUtils from '@/hooks/useBetSdkUtils'
import { AppEvents, useAppEvent } from '@/hooks/useEventEmitter'
import { EFinality, useToastTransaction } from '@/hooks/useToastify'
import { useWallet } from '@/hooks/useWallet'
import {
  DEFAULT_DECIMALS,
  EnumSwapBase,
  IComputeData,
  NATIVE_MINT
} from '@/lib/betSdkUtils'
import { filterPositive } from '@/lib/format'
import useSolana from '@/lib/solana/hooks/useSolana'
import { cn, max2Min, min2Max } from '@/lib/utils'
import { useGlobalMethodProviderContext } from '@/providers/GlobalMethodProvider'
import { loginStatusAtom, tokenInfoAtom } from '@/stores/services'

import { BorderBox, Flex, Grid } from '../common/Box'
import {
  AsyncButton,
  ButtonRadioGroup,
  SecondaryButton
} from '../common/Button'
import { NumberInput } from '../common/Input'
import { Exo2Text, OrbitronText, Text, TipsText } from '../common/Text'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem
} from '../ui/form'

import PopupSlippage from './PopupSlippage'

const formSchema = z.object({
  tradeType: z.enum([TradeType.BUY, TradeType.SELL]),
  amount: z.string() // 字符串类型
})

const Trade: React.FC<{ mint: string; txSetting: ITxSettingProps }> = ({
  mint,
  txSetting
}) => {
  const { t } = useTranslation()
  const { connect: connectWallet } = useWallet()
  const { signMessageWithLogin } = useGlobalMethodProviderContext() || {}

  const { isConnected, address } = useSolana()
  const { betSdkUtilRef } = useBetSdkUtils()
  const hookAppEvent = useAppEvent()

  const loginStatus = useAtomValue(loginStatusAtom)
  const tokenInfo = useAtomValue(tokenInfoAtom)
  const toaster = useToastTransaction()

  const computeDataRef = useRef<IComputeData>() // 兑换计算结果
  const solTokenRef = useRef<ITradeToken>({
    mint: NATIVE_MINT.toBase58(),
    symbol: 'SOL',
    icon: SolanaSrc,
    balance: '0',
    decimals: 9
  })
  const coinTokenRef = useRef<ITradeToken>({
    mint: mint,
    symbol: 'TOKEN',
    icon: Logo,
    balance: '0',
    decimals: DEFAULT_DECIMALS
  })

  // const [tokenBalance, setTokenBalance] = useState<any>()
  const [tokenRefTrigger, setTokenRefTrigger] = useState({})
  const [estimateData, setEstimateData] = useState<{
    payAmount: string
    receiveAmount: string
    text: string
  }>()
  const [tradeBase, setTradeBase] = useState(TradeBase.SOL)
  const [swapPending, setSwapPending] = useState<boolean>(false) // 兑换等待状态
  const [estimatePending, setEstimatePending] = useState<boolean>(false) // 计算等待状态
  const [balancePending, setBalancePending] = useState<boolean>(false) // 计算等待状态
  const [slippagePopup, setSlippagePopup] = useState<boolean>(false)

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      tradeType: TradeType.BUY,
      amount: ''
    }
  })
  const tradeType = form.watch('tradeType')
  const amount = form.watch('amount')

  // const { ref:amountRef, fontSize:fontSizeAmount } = useAutoFontSize(amount, { min: 10, max: 20, width: 100 })

  interface ITradeToken {
    mint: string
    symbol: string
    icon: string
    decimals: number
    balance: string
  }

  const memoForm = useMemo(
    () => {
      let supportValues: any = []
      let tokenFrom
      let tokenTo
      let tokenBase
      if (tradeType === TradeType.BUY) {
        tokenFrom = solTokenRef.current
        tokenTo = coinTokenRef.current
        if (tradeBase === TradeBase.SOL) {
          tokenBase = solTokenRef.current
          supportValues = [SupportValue.Reset, 0.1, 0.5, 1, SupportValue.Max]
        } else {
          tokenBase = coinTokenRef.current
          supportValues = []
        }
      } else {
        tokenFrom = coinTokenRef.current
        tokenTo = solTokenRef.current
        // tokenBase = coinTokenRef.current
        // supportValues = [SupportValue.Reset, 25, 50, 75, 100]
        if (tradeBase === TradeBase.SOL) {
          tokenBase = solTokenRef.current
          supportValues = []
        } else {
          tokenBase = coinTokenRef.current
          supportValues = [SupportValue.Reset, 25, 50, 75, 100]
        }
      }
      return {
        tokenFrom,
        tokenTo,
        tokenBase,
        supportValues
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tradeType, tradeBase, tokenRefTrigger]
  )

  const memoBtnConfirm = useMemo(() => {
    const tokenFrom = memoForm.tokenFrom
    const payValue = Number(estimateData?.payAmount ?? 0)
    const balance = Number(tokenFrom.balance)
    const disabled = swapPending || payValue <= 0 || payValue > balance
    return {
      disabled,
      insufficient: balance < payValue
    }
  }, [memoForm, swapPending, estimateData])

  const getSolBalance = useCallback(async () => {
    const token = solTokenRef.current
    const tradeProvider = betSdkUtilRef.current?.tradeProvider
    if (!tradeProvider || !tradeProvider.publicKey) {
      token.balance = '0.00'
      return
    }
    const resp = await tradeProvider.connection.getBalance(
      tradeProvider.publicKey
    )
    token.balance = Number(resp) > 0 ? min2Max(resp, token.decimals) : '0.00'
  }, [betSdkUtilRef])

  const getCoinBalance = useCallback(async () => {
    const token = coinTokenRef.current
    if (!betSdkUtilRef.current || !token.mint) {
      token.balance = '0.00'
      return
    }
    const resp = await betSdkUtilRef.current
      .getTokenBalance(token.mint)
      .catch(() => undefined)
    token.balance =
      resp && Number(resp.amount) > 0
        ? min2Max(resp.amount, token.decimals)
        : '0.00'
  }, [betSdkUtilRef])

  const refreshBalance = useCallback(async () => {
    setBalancePending(true)
    Promise.allSettled([
      tradeType === TradeType.BUY ? getSolBalance() : getCoinBalance()
    ]).finally(() => {
      setBalancePending(false)
      setTokenRefTrigger({})
    })
  }, [tradeType, getSolBalance, getCoinBalance])

  const handleSupportValue = useCallback(
    (val: number | SupportValue) => {
      const balance = memoForm.tokenFrom.balance
      if (Number(balance) == 0) {
        form.setValue('amount', '')
        return
      }
      if (val === SupportValue.Max) {
        form.setValue('amount', balance)
        return
      }
      if (val === SupportValue.Reset) {
        form.setValue('amount', '')
        return
      }
      if (tradeType === TradeType.BUY) {
        form.setValue('amount', val + '')
      } else {
        const decimals = memoForm.tokenFrom.decimals
        const value = BigNumber(balance)
          .multipliedBy(val)
          .dividedBy(100)
          .toFixed(decimals)
        form.setValue('amount', value)
      }
    },
    [memoForm.tokenFrom, form, tradeType]
  )

  const handleSendTranscation = useCallback(
    async (tradeTx: any) => {
      const tradeProvider = betSdkUtilRef.current?.tradeProvider
      if (!tradeProvider) {
        return
      }
      const rpcCurrent = SUPPORT_RPC_ENDPOINT.find(
        (item) => item.url === tradeProvider.connection.rpcEndpoint
      )
      const tradeSetting = betSdkUtilRef.current?.txSetting
      const signedTx = await tradeProvider.wallet.signTransaction(tradeTx.tx)

      let tradePromise
      if (tradeSetting?.mevState && rpcCurrent?.mev) {
        tradePromise = tradeProvider.sendMevProtectionTransaction(signedTx)
      } else {
        tradePromise = tradeProvider.sendSignedTransaction(signedTx)
      }

      toaster.processing()
      const txHash = await tradePromise
      // 等待confirmed状态
      const resp = await tradeProvider
        .confirmTransaction(txHash, {
          latestBlockhash: tradeTx?.latestBlockhash,
          finality: EFinality.CONFIRMED,
          report:
            betSdkUtilRef.current?.betSdkInstance?.program?.programId?.toBase58(),
          onConfirmed: () => {
            setEstimateData(undefined)
            refreshBalance()
            form.setValue('amount', '')
            toaster.finalized({ txHash })
          },
          onError: (error) => {
            toaster.failed({ txHash, error })
          }
        })
        .catch(() => undefined)

      if (resp) {
        // 等待finalized状态
        tradeProvider.confirmTransaction(txHash, {
          latestBlockhash: tradeTx?.latestBlockhash,
          finality: EFinality.FINALIZED,
          onFinalized: () => {
            refreshBalance()
            hookAppEvent.emit(AppEvents.TOKEN_TRADE)
          }
        })
      }

      // let respTx: any = {}
      // const toastPromise = tradePromise.then((hash) => {
      //   respTx = {
      //     txHash: hash,
      //     latestBlockhash: tradeTx.latestBlockhash
      //   }
      //   reportTxHash.trigger({
      //     txId: hash,
      //     programId: betSdkUtilRef.current?.betSdkInstance?.program.programId
      //   })
      //   return respTx
      // })
      // await toastConfirmTransaction(toastPromise)

      // const txHash = await tradePromise
      // await confirmTransactionStatus(
      //   [txHash],
      //   {
      //     finality: EFinality.CONFIRMED,
      //     latestBlockhash: respTx.latestBlockhash
      //   }
      // )
      // reportTxHash.trigger({
      //   txId: hash,
      //   programId: betSdkUtilRef.current?.betSdkInstance?.program.programId
      // })
      // setEstimateData(undefined)
      // refreshBalance()
      // form.setValue('amount', '')
      // // 获取finalizedz状态
      // confirmTransactionStatus(
      //   [respTx.txHash],
      //   {
      //     finality: EFinality.FINALIZED,
      //     latestBlockhash: respTx.latestBlockhash
      //   }
      // ).then(() => {
      //   refreshBalance()
      //   hookAppEvent.emit(AppEvents.TOKEN_TRADE)
      //   // setTradeEvent({ txHash: Date.now().toString() })
      // })
    },
    [betSdkUtilRef, form, hookAppEvent, refreshBalance]
  )

  const updateComputeData = useCallback(
    (data: IComputeData | undefined, memoForm?: any) => {
      computeDataRef.current = data
      if (data && memoForm) {
        let tokenEstimate, estimateValue, estimateLabel
        if (memoForm.tokenBase === memoForm.tokenFrom) {
          estimateLabel = t('coin.trade.receive')
          tokenEstimate = memoForm.tokenTo
          estimateValue = data.outputAmount
        } else {
          estimateLabel = t('coin.trade.cost')
          tokenEstimate = memoForm.tokenFrom
          estimateValue = data.inputAmount
        }
        const estimateAmount = min2Max(estimateValue, tokenEstimate.decimals)
        setEstimateData({
          payAmount: min2Max(data.payAmount, memoForm.tokenFrom.decimals),
          receiveAmount: estimateAmount,
          text: `${estimateLabel}: ${estimateAmount} ${tokenEstimate.symbol}`
        })
      } else {
        setEstimateData(undefined)
      }
    },
    [t]
  )

  const onSubmit = useCallback(
    async (values: z.infer<typeof formSchema>) => {
      if (!betSdkUtilRef.current) {
        return
      }
      if (loginStatus !== LoginStatus.LOGIN) {
        const resp = await signMessageWithLogin()
        if (!resp) {
          return
        }
      }
      setSwapPending(true)
      let tradeTx
      const swapBase = computeDataRef.current!.swapType
      if (values.tradeType === TradeType.BUY) {
        if (swapBase === EnumSwapBase.IN) {
          const amount = computeDataRef.current!.payAmount
          tradeTx = await betSdkUtilRef.current.buyTokenBySolAmount(
            mint,
            amount
          )
        } else if (swapBase === EnumSwapBase.OUT) {
          const amount = computeDataRef.current!.outputAmount
          tradeTx = await betSdkUtilRef.current.buyTokenByTokenAmount(
            mint,
            amount
          )
        }
      } else if (values.tradeType === TradeType.SELL) {
        if (swapBase === EnumSwapBase.IN) {
          const amount = computeDataRef.current!.payAmount
          tradeTx = await betSdkUtilRef.current.sellTokenByTokenAmount(
            mint,
            amount
          )
        } else if (swapBase === EnumSwapBase.OUT) {
          const amount = computeDataRef.current!.outputAmount
          tradeTx = await betSdkUtilRef.current.sellTokenBySolAmount(
            mint,
            amount
          )
        }
      }
      await handleSendTranscation(tradeTx)
        .catch((err) => {
          toast.error(err?.message ?? err?.msg ?? err)
        })
        .finally(() => {
          setSwapPending(false)
        })
    },
    [
      betSdkUtilRef,
      loginStatus,
      handleSendTranscation,
      signMessageWithLogin,
      mint
    ]
  )

  const onChangeTradeType = (type: TradeType) => {
    form.setValue('tradeType', type)
    updateComputeData(undefined)
    if (type === TradeType.BUY) {
      setTradeBase(TradeBase.SOL)
    } else {
      setTradeBase(TradeBase.TOKEN)
    }
  }

  const onChangeTradeAmount = (value: string) => {
    const val = filterPositive(value, memoForm.tokenBase.decimals)
    form.setValue('amount', val)
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const calcEstimateAmount = useCallback(
    debounce(
      (() => {
        let sign = undefined // 当前调用任务标识
        const handleEmptyValue = () => {
          updateComputeData(undefined)
        }

        return async (value: string, memoForm: any) => {
          const signCurrent = Date.now()
          sign = signCurrent
          if (!(Number(value) > 0)) {
            handleEmptyValue()
            return
          }
          if (!betSdkUtilRef.current) {
            handleEmptyValue()
            return
          }
          const tokenFrom: ITradeToken = memoForm.tokenFrom
          const tokenTo: ITradeToken = memoForm.tokenTo
          const tokenBase: ITradeToken = memoForm.tokenBase
          // const slippageBps = Number(txSetting.slippage)
          const params = {
            base: tokenBase === tokenFrom ? EnumSwapBase.IN : EnumSwapBase.OUT,
            inputMint: tokenFrom.mint,
            outputMint: tokenTo.mint,
            amount: max2Min(value, tokenBase.decimals)
            // slippageBps: slippageBps
          }
          // handleEmptyValue()
          setEstimatePending(true)
          const computeData = await betSdkUtilRef.current
            .getComputeData(params)
            .catch(() => undefined)
          // console.log('computeData', computeData)
          if (signCurrent !== sign || tokenBase !== memoForm.tokenBase) {
            // 已有新的状态改变，中止当前计算以保证只响应最后一次计算
            return
          }
          setEstimatePending(false)
          if (!computeData) {
            handleEmptyValue()
            return
          }
          updateComputeData(computeData, memoForm)
        }
      })(),
      500
    ),
    []
  )

  useEffect(() => {
    refreshBalance()
  }, [isConnected, refreshBalance])

  useEffect(() => {
    form.setValue('amount', '')
  }, [form, tradeType, tradeBase])

  useEffect(() => {
    calcEstimateAmount(amount, memoForm)
  }, [amount])

  useEffect(() => {
    if (tokenInfo) {
      coinTokenRef.current.symbol = tokenInfo?.token_symbol
      coinTokenRef.current.icon = tokenInfo?.token_image
    }
    setTokenRefTrigger({})
  }, [tokenInfo])

  return (
    <BorderBox data-testid="tradeForm">
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className="space-y-4 xl:space-y-8"
        >
          <Grid className="gap-3">
            <FormField
              control={form.control}
              name="tradeType"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <ButtonRadioGroup
                      className="bg-transparent p-1 [&>.item]:h-10 [&>.item]:flex-1 [&_label]:!text-base"
                      variant="secondary"
                      options={[
                        { label: t('coin.trade.buy'), value: TradeType.BUY },
                        { label: t('coin.trade.sell'), value: TradeType.SELL }
                      ]}
                      value={field.value}
                      onValueChange={onChangeTradeType}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            {/* <Flex className="justify-between [&_button]:text-xs [&_button]:text-primary">
              <SecondaryButton
                data-testid="buttonSwitch"
                size="xs"
                className={cn(theme === Theme.Light && 'bg-primary/10')}
                border={false}
                onClick={onChangeTradeBase}
              >
                {t('coin.trade.switchTo', {
                  var1:
                    tradeBase === TradeBase.SOL
                      ? coinTokenRef.current.symbol
                      : solTokenRef.current.symbol
                })}
              </SecondaryButton>
            </Flex> */}
          </Grid>

          <FormField
            control={form.control}
            name="amount"
            render={({ field }) => (
              <FormItem>
                <Flex className="items-center justify-between">
                  <OrbitronText
                    className="pr-2 text-sm"
                    onClick={refreshBalance}
                    variant="secondary"
                  >
                    {t('coin.trade.amount')}
                  </OrbitronText>

                  <Flex className="gap-1">
                    <Flex
                      data-testid="balanceText"
                      className="font-Orbitron cursor-pointer items-center gap-1 text-sm text-white/80"
                      onClick={refreshBalance}
                    >
                      <Iconloading
                        className="inline-block size-5 opacity-60"
                        style={{
                          visibility: balancePending ? 'visible' : 'hidden'
                        }}
                      />
                      <Wallet className="size-5" />
                      <span>{memoForm.tokenFrom.balance}</span>
                    </Flex>
                    <SecondaryButton
                      data-testid="buttonSlippag"
                      className="gap-1 rounded-md bg-[rgba(123,97,255,0.1)] p-1"
                      size="xs"
                      border={false}
                      onClick={() => setSlippagePopup(true)}
                    >
                      <Slip />
                      <Text>{txSetting.slippage}%</Text>
                    </SecondaryButton>
                  </Flex>
                </Flex>
                <FormControl>
                  <NumberInput
                    size="lg"
                    className="text-right font-Exo2"
                    data-testid="amountText"
                    wrapperClassName="[&_input]:text-base"
                    {...(tradeType === TradeType.BUY
                      ? tradeBase === TradeBase.SOL
                        ? { value: field.value }
                        : { value: estimateData?.receiveAmount || '' }
                      : tradeBase === TradeBase.TOKEN
                        ? { value: field.value }
                        : { value: estimateData?.receiveAmount || '' })}
                    onFocus={() => {
                      setEstimateData(undefined)
                      setTradeBase(
                        tradeType === TradeType.BUY
                          ? TradeBase.SOL
                          : TradeBase.TOKEN
                      )
                    }}
                    onChange={(event) => {
                      onChangeTradeAmount(event.target.value)
                    }}
                    prefixNode={
                      <Flex className="items-center">
                        <img
                          src={
                            tradeType === TradeType.BUY
                              ? solTokenRef.current.icon
                              : coinTokenRef.current.icon
                          }
                          alt="token icon"
                          className="size-6 rounded-full object-cover xl:size-8"
                        />
                        <Exo2Text
                          className="ml-1 text-base"
                          data-testid="prefixToken"
                        >
                          {tradeType === TradeType.BUY
                            ? solTokenRef.current.symbol
                            : coinTokenRef.current.symbol}
                        </Exo2Text>
                      </Flex>
                    }
                    placeholder="0.00"
                  />
                </FormControl>
              </FormItem>
            )}
          />
          <Flex className="!-my-2.5 items-center justify-center">
            <Flex className="h-6 w-16 items-center justify-center rounded-full border bg-background">
              <ChevronDown className="text-primary" />
            </Flex>
          </Flex>
          <FormField
            control={form.control}
            name="amount"
            render={({ field }) => (
              <FormItem className="!mt-0">
                <FormControl>
                  <NumberInput
                    size="lg"
                    className="text-right font-Exo2"
                    wrapperClassName="[&_input]:text-base"
                    {...(tradeType === TradeType.BUY
                      ? tradeBase === TradeBase.SOL
                        ? { value: estimateData?.receiveAmount || '' }
                        : { value: field.value }
                      : tradeBase === TradeBase.TOKEN
                        ? { value: estimateData?.receiveAmount || '' }
                        : { value: field.value })}
                    onFocus={() => {
                      setEstimateData(undefined)
                      setTradeBase(
                        tradeType === TradeType.BUY
                          ? TradeBase.TOKEN
                          : TradeBase.SOL
                      )
                    }}
                    onChange={(event) => {
                      onChangeTradeAmount(event.target.value)
                    }}
                    prefixNode={
                      <Flex className="items-center">
                        <img
                          src={
                            tradeType === TradeType.BUY
                              ? coinTokenRef.current.icon
                              : solTokenRef.current.icon
                          }
                          alt="token icon"
                          className="size-6 rounded-full object-cover xl:size-8"
                        />
                        <Exo2Text
                          className="ml-1 text-base"
                          data-testid="prefixToken"
                        >
                          {tradeType === TradeType.BUY
                            ? coinTokenRef.current.symbol
                            : solTokenRef.current.symbol}
                        </Exo2Text>
                      </Flex>
                    }
                    placeholder="0.00"
                  />
                </FormControl>
                <FormDescription className="flex justify-between gap-2">
                  {memoForm.supportValues.map((val: any) => (
                    <SecondaryButton
                      data-testid="amountSetButton"
                      size="xs"
                      border={false}
                      key={val}
                      className={cn(
                        'flex-1 px-0 text-xs text-primary xl:text-sm',
                        'bg-[rgba(123,97,255,0.1)]'
                      )}
                      onClick={() => handleSupportValue(val)}
                    >
                      {val === SupportValue.Reset
                        ? t('coin.trade.reset')
                        : val === SupportValue.Max
                          ? t('coin.trade.max')
                          : tradeType === TradeType.BUY
                            ? val + ' ' + memoForm.tokenFrom.symbol
                            : val + '%'}
                    </SecondaryButton>
                  ))}
                </FormDescription>
                <TipsText
                  variant="destructive"
                  data-testid="insufficientToken"
                  className="bg-transparent p-0"
                  style={{
                    display: memoBtnConfirm.insufficient ? 'flex' : 'none'
                  }}
                >
                  {t('common.insufficientToken', {
                    var1: memoForm.tokenFrom.symbol
                  })}
                </TipsText>
              </FormItem>
            )}
          />

          <Grid className="gap-2">
            <Text
              data-testid="estimateText"
              className="flex items-center"
              style={{
                display:
                  estimatePending || estimateData?.text ? 'block' : 'none'
              }}
            >
              {estimateData?.text}
              <Iconloading
                className="mx-1 inline-block size-5 opacity-60"
                style={{ visibility: estimatePending ? 'visible' : 'hidden' }}
              />
            </Text>

            <AsyncButton
              data-testid="confirmButton"
              className="font-Orbitron"
              type={isConnected ? 'submit' : 'button'}
              loading={swapPending}
              disabled={isConnected && memoBtnConfirm.disabled}
              onClick={
                !isConnected
                  ? (ev) => {
                      ev.stopPropagation()
                      ev.preventDefault()
                      connectWallet()
                    }
                  : undefined
              }
            >
              {!isConnected ? t('common.connectWallet') : t('coin.trade.place')}
            </AsyncButton>
          </Grid>
        </form>
      </Form>

      <PopupSlippage open={slippagePopup} onOpenChange={setSlippagePopup} />
    </BorderBox>
  )
}

export default Trade
